import paper from 'paper';
import { Bezier } from 'bezier-js';

const getDistance = (point1, point2) => Math.hypot(point1.x - point2.x, point1.y - point2.y);

const interpolatePoints = (point1, point2, ratio) => ({
    x: point1.x + ratio * (point2.x - point1.x),
    y: point1.y + ratio * (point2.y - point1.y)
});

const calculateSlope = (p1, p2) => {
    if (p1.x === p2.x && p1.y === p2.y) return undefined;
    if (p1.x === p2.x) return Infinity;
    if (p1.y === p2.y) return 0;
    return (p1.y - p2.y) / (p1.x - p2.x);
};

const calculateLineEquation = (e, t) => {
    const slope = calculateSlope(e, t);
    return {
        slope: slope,
        intercept: slope === 1 / 0 || void 0 === slope ? void 0 : t.y - slope * t.x
    };
};

const getAngleFromPoints = (point1, point2) => {
    const distance = getDistance(point1, point2);
    let angle = Math.acos((point2.x - point1.x) / distance);
    if (point2.y - point1.y < 0) angle = 2 * Math.PI - angle;
    return angle;
};

// Helper function to determine if a point is on the left side of a line
function isPointOnLeft(point, topPoint, bottomPoint) {
    const { x, y } = point;
    const { slope, intercept } = calculateLineEquation(topPoint, bottomPoint);

    // Default to true (left side)
    if (slope === undefined) {
        return true;
    }

    // Check various cases to determine if point is not on left side
    if (
        (slope === Infinity && x > topPoint.x) ||
        (slope > 0 && slope * x + intercept > y) ||
        (slope < 0 && slope * x + intercept < y) ||
        (slope === 0 && y < topPoint.y)
    ) {
        return false;
    }

    return true;
}

/**
 * Determines if an angle is within a specific range
 * @param {Object} testPoint - Point to test
 * @param {Object} pivotPoint - Point to measure angle from
 * @param {Object} referencePoint - Point that defines the reference angle
 * @returns {boolean} True if angle is within range [0, π)
 */
function isAngleInRange(testPoint, pivotPoint, referencePoint) {
    const angleDifference = getAngleFromPoints(pivotPoint, testPoint) - getAngleFromPoints(pivotPoint, referencePoint);

    return angleDifference >= 0 && angleDifference < Math.PI;
}

class BezierCurve extends Bezier {
    constructor() {
        super(...arguments);
        this.len = this.length();
        this._flattened = this._getFlattenedCurve();
    }

    _getFlattenedCurve() {
        const curveData = {
            points: [],
            stepLengths: []
        };
        for (let i = 0; i <= 100; i++) {
            const point = this.get(i / 100);
            curveData.points.push(point);

            // Calculate the distance between the current point and the previous point
            if (i > 0) {
                const distance = getDistance(curveData.points[i - 1], point);

                // Calculate the sum of the distances
                if (1 === i) {
                    curveData.stepLengths.push({
                        self: distance,
                        sum: distance
                    });
                } else {
                    curveData.stepLengths.push({
                        self: distance,
                        sum: curveData.stepLengths[i - 2].sum + distance
                    });
                }
            }
        }
        return curveData;
    }

    /**
     * Projects a point onto the curve by finding the closest point through recursive subdivision
     * @param {Object} point - The point to project {x: number, y: number}
     * @param {Array} initialPoints - Array of points with t values to start subdivision from
     * @returns {Object} The closest point found on the curve
     */
    _getProjectedPoint(point, initialPoints) {
        const { x, y } = point;
        let currentPoints = initialPoints;
        let currentIndex = 0;
        let closestPoint = currentPoints[currentIndex];
        let iterations = 1;
        let minDistance = Infinity;

        // Maximum number of iterations to prevent infinite loops
        const MAX_ITERATIONS = 20;

        // Minimum distance between t values to stop subdivision
        const MIN_DELTA_T = 1e-4;

        do {
            // Get previous and next indices, handling array bounds
            const prevIndex = Math.max(0, currentIndex - 1);
            const nextIndex = Math.min(currentPoints.length - 1, currentIndex + 1);

            // Calculate start t value and subdivision step
            let startT = currentPoints[prevIndex].t;
            let deltaT = (currentPoints[nextIndex].t - startT) / 4;
            if (deltaT < MIN_DELTA_T) break;

            let newPoints = [currentPoints[prevIndex]];
            for (let i = 1; i <= 3; i++) {
                const newT = startT + i * deltaT;
                const newPoint = this.get(newT);
                newPoint.distance = getDistance(
                    {
                        x: newPoint.x,
                        y: newPoint.y
                    },
                    {
                        x,
                        y
                    }
                );
                if (newPoint.distance < minDistance) {
                    minDistance = newPoint.distance;
                    closestPoint = newPoint;
                    currentIndex = i;
                }
                newPoints.push(newPoint);
            }

            // Add the last point to the new points array
            newPoints.push(currentPoints[nextIndex]);
            currentPoints = newPoints;
            iterations++;
        } while (iterations < MAX_ITERATIONS);
        return closestPoint;
    }

    /**
     * Gets a point on the curve at a specified length ratio (0 to 1)
     * @param {number} ratio - The ratio along the curve length (0 = start, 1 = end)
     * @returns {Point} The point on the curve at the specified length ratio
     */
    getPointAtLengthRatio(ratio) {
        const flattened = this._flattened;
        // TODO: need improvement with cached values

        // Calculate target length along the curve
        const totalLength = flattened.stepLengths[flattened.stepLengths.length - 1].sum;
        const targetLength = ratio * totalLength;

        // Find the segment containing the target length
        let segmentIndex = 0;
        while (segmentIndex < flattened.stepLengths.length) {
            const currentSum = flattened.stepLengths[segmentIndex].sum;
            if (targetLength <= currentSum || segmentIndex === flattened.stepLengths.length - 1) {
                break;
            }
            segmentIndex++;
        }

        const segmentLength = flattened.stepLengths[segmentIndex].self;
        const previousSum = segmentIndex > 0 ? flattened.stepLengths[segmentIndex - 1].sum : 0;

        // Calculate how far along this segment the target point lies
        const segmentRatio = (targetLength - previousSum) / segmentLength;

        // Interpolate between the segment's endpoints
        const interpolatedPoint = interpolatePoints(
            flattened.points[segmentIndex],
            flattened.points[segmentIndex + 1],
            segmentRatio
        );

        // Project the interpolated point onto the actual curve
        return this._getProjectedPoint(interpolatedPoint, flattened.points.slice(segmentIndex, segmentIndex + 2));
    }
}

class WarpTransformer {
    constructor(options) {
        this.points = options.points;
        this.bounds = this.prepareBounds(options.bounds);
        this.transformConvex = this.getTransformConvex(options.points);
    }

    prepareBounds(boundingBox) {
        return [
            {
                x: boundingBox.minX,
                y: -boundingBox.maxY
            },
            {
                x: boundingBox.maxX,
                y: -boundingBox.maxY
            },
            {
                x: boundingBox.maxX,
                y: -boundingBox.minY
            },
            {
                x: boundingBox.minX,
                y: -boundingBox.minY
            }
        ];
    }

    getTransformConvex(points) {
        const [p0, p1, p2, p3, p4, p5, p6, p7, p8, p9] = points;
        return {
            top: {
                breakpoint: p1,
                left: new BezierCurve(p0, p0, p6, p1), // Control points: Start->Start->Handle->End
                right: new BezierCurve(p1, p7, p2, p2) // Control points: Start->Handle->End->End
            },
            bottom: {
                breakpoint: p4,
                left: new BezierCurve(p5, p5, p8, p4), // Control points: Start->Start->Handle->End
                right: new BezierCurve(p4, p9, p3, p3) // Control points: Start->Handle->End->End
            }
        };
    }

    /**
     * Calculates break points on the top and bottom bounds based on transform convex proportions
     * @returns {Object} Break points for top and bottom bounds
     */
    getBreakPointsOnBounds() {
        // Helper function to calculate break point along a line
        const calculateBreakPoint = (startPoint, endPoint, leftLen, rightLen) => {
            const ratio = leftLen / (leftLen + rightLen);
            return {
                x: startPoint.x + ratio * (endPoint.x - startPoint.x),
                y: startPoint.y
            };
        };
        const { bounds, transformConvex } = this;

        return {
            top: calculateBreakPoint(
                bounds[0], // Start point
                bounds[1], // End point
                transformConvex.top.left.len, // Left segment length
                transformConvex.top.right.len // Right segment length
            ),
            bottom: calculateBreakPoint(
                bounds[3], // Start point
                bounds[2], // End point
                transformConvex.bottom.left.len, // Left segment length
                transformConvex.bottom.right.len // Right segment length
            )
        };
    }

    /**
     * Calculates the x-ratio of a point relative to break points and bounds
     * @param {Point} point - The point to calculate ratio for
     * @param {BreakPoints} breakPoints - The break points defining the segments
     * @returns {number} The x-ratio of the point
     */
    getXRatio(point, breakPoints) {
        let { x, y } = point;
        const isLeft = isPointOnLeft({ x, y }, breakPoints.top, breakPoints.bottom);
        const { slope, intercept } = calculateLineEquation(breakPoints.top, breakPoints.bottom);

        let ratio = 0;
        if (isLeft) {
            // Calculate ratio for left side
            const startX = this.bounds[0].x;
            const breakX = Math.abs(slope) === Infinity ? breakPoints.top.x : (y - intercept) / slope;
            ratio = (x - startX) / (breakX - startX);
        } else {
            // Calculate ratio for right side
            const breakX = Math.abs(slope) === Infinity ? breakPoints.top.x : (y - intercept) / slope;
            ratio = (x - breakX) / (this.bounds[1].x - breakX);
        }

        return ratio;
    }

    /**
     * Transforms a point based on top and bottom curves at a given ratio
     * @param {Object} sourcePoint - The original point to transform
     * @param {Object} curves - Object containing top and bottom curve definitions
     * @param {number} ratio - The length ratio along the curves to sample points from
     * @returns {Object} The interpolated point between the curves
     */
    getTransformedPoint(sourcePoint, curves, ratio) {
        let { y: sourceY } = sourcePoint;

        // Get points at the specified ratio along both curves
        const topCurvePoint = curves.top.getPointAtLengthRatio(ratio);
        const bottomCurvePoint = curves.bottom.getPointAtLengthRatio(ratio);

        // Calculate the vertical interpolation factor based on source point's position
        // between the top and bottom bounds
        const verticalFactor = (sourceY - this.bounds[0].y) / (this.bounds[3].y - this.bounds[0].y);

        // Interpolate between the curve points based on the vertical factor
        return interpolatePoints(topCurvePoint, bottomCurvePoint, verticalFactor);
    }

    /**
     * Transforms coordinates from one space to another using breakpoints and convex regions
     * @param {string} inputCoordinates - Space-separated x y coordinates (e.g., "100 200")
     * @returns {string} Transformed coordinates in the same format
     */
    getTransformedCoordinatesString(inputCoordinates) {
        // Parse input coordinates
        const [x, y] = inputCoordinates.split(' ').map(Number);
        const point = { x, y };

        // Get break points that divide the transformation regions
        const breakPoints = this.getBreakPointsOnBounds();

        // Check if point's angle is within the valid range
        const isInLeftRegion = isAngleInRange(point, breakPoints.top, breakPoints.bottom);

        // Calculate the x-ratio for the transformation
        const xRatio = this.getXRatio(point, breakPoints);

        // Determine which convex region to use based on angle check
        const transformRegion = isInLeftRegion
            ? {
                  top: this.transformConvex.top.left,
                  bottom: this.transformConvex.bottom.left,
                  points: [
                      this.points[0],
                      this.transformConvex.top.breakpoint,
                      this.transformConvex.bottom.breakpoint,
                      this.points[5]
                  ]
              }
            : {
                  top: this.transformConvex.top.right,
                  bottom: this.transformConvex.bottom.right,
                  points: [
                      this.transformConvex.top.breakpoint,
                      this.points[2],
                      this.points[3],
                      this.transformConvex.bottom.breakpoint
                  ]
              };

        // Transform the point using the selected region
        const transformedPoint = this.getTransformedPoint(point, transformRegion, xRatio);

        // Return the transformed coordinates in the same format as input
        return `${transformedPoint.x} ${transformedPoint.y}`;
    }
}

const createSegment = (point, control1, control2) => {
    const basePoint = new paper.Point(point || { x: 0, y: 0 });
    const handleIn = control1 ? new paper.Point(control1).subtract(basePoint) : undefined;
    const handleOut = control2 ? new paper.Point(control2).subtract(basePoint) : undefined;

    return new paper.Segment(basePoint, handleIn, handleOut);
};

const drawLinePath = (point, ctx) => {
    let { start, end } = point;
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
};

const createCurvePath = (controlPoints) => {
    let [[p1, n1, r1], [p2, n2, r2], [p3, n3, r3]] = controlPoints;
    const path = new paper.Path();
    path.add(createSegment(p1, n1, r1));
    path.add(createSegment(p2, n2, r2));
    path.add(createSegment(p3, n3, r3));
    return path;
};

const createPathSegments = (points) => {
    const [p0, p1, p2, p3, p4, p5, p6, p7, p8, p9] = points;

    return {
        top: createCurvePath([
            [p0, null, null],
            [p1, p6, p7],
            [p2, null, null]
        ]),
        bottom: createCurvePath([
            [p5, null, null],
            [p4, p8, p9],
            [p3, null, null]
        ])
    };
};

const drawSmoothPath = (path, ctx) => {
    const startPoint = path.getPointAt(0);
    const endPoint = path.getPointAt(path.length);
    ctx.moveTo(startPoint.x, startPoint.y);
    for (let index = 0; index <= 1; index += 0.01) {
        const pointAtPath = path.getPointAt(index * path.length);
        if (pointAtPath) {
            ctx.lineTo(pointAtPath.x, pointAtPath.y);
        }
    }
    ctx.lineTo(endPoint.x, endPoint.y);
};

const applyCanvasTransform = (x, y, canvas) => {
    if (!canvas) return new fabric.Point(x, y);
    const zoomLevel = canvas.getZoom();
    const viewportTransform = canvas.viewportTransform;
    const offsetX = viewportTransform[4];
    const offsetY = viewportTransform[5];
    return new fabric.Point(x * zoomLevel + offsetX, y * zoomLevel + offsetY);
};

function positionHandler(_dim, _finalMatrix, fabricObject) {
    const point = fabricObject.points[this.pointIndex];
    return applyCanvasTransform(point.x, point.y, fabricObject.canvas);
}

const alignPoint = (current, target) => {
    const aligned = { ...current };
    Math.abs(target.x - current.x) > Math.abs(target.y - current.y) ? (aligned.y = target.y) : (aligned.x = target.x);
    return aligned;
};

function createShiftConstraint(handler, pointIndex) {
    return function (event, data, newX, newY) {
        const referencePoint = data.target.lastPoints[pointIndex];
        let newPosition = { x: newX, y: newY };
        if (event.shiftKey) {
            newPosition = alignPoint(newPosition, referencePoint);
        }
        return handler(event, data, newPosition.x, newPosition.y);
    };
}

// Delay para 30fps (1000ms / 30 ≈ 33.33ms)
const FAST_DEBOUNCE_DELAY = 1000 / 30; // Aproximadamente 30fps
const UPDATE_DEBOUNCE_DELAY = 500;

function transformPointsDebounced(target) {
    // Schedule transform update
    clearTimeout(target.__setTransformPointsTimeoutId);
    clearTimeout(target.__setTransformPointsUpdateId);

    // Update the transform points
    target.__setTransformPointsUpdateId = setTimeout(() => {
        target.setTransformPoints(target.points, {
            ...target.transformOptions,
            isChanging: false
        });
    }, UPDATE_DEBOUNCE_DELAY);

    target.__setTransformPointsTimeoutId = setTimeout(() => {
        target.setTransformPoints(target.points, {
            ...target.transformOptions,
            isChanging: true
        });
    }, FAST_DEBOUNCE_DELAY);
}

export const WarpFormControl = {
    line: (fabric) => {
        return new fabric.Control({
            actionName: 'showTransform',
            lineIndex: 0,
            render: (ctx, _left, _top, _styleOverride, fabricObject) => {
                if (!fabricObject.points.length) return;
                const canvas = fabricObject.canvas;
                if (!canvas) return;
                const viewportTransform = canvas.viewportTransform;
                const transformedPoints = fabricObject.points.map((point) => {
                    const pointInstance = new fabric.Point(point.x, point.y);
                    return fabric.util.transformPoint(pointInstance, viewportTransform);
                });
                ctx.save();
                ctx.beginPath();
                ctx.lineWidth = 1;
                ctx.strokeStyle = 'rgba(79,128,255,1)';
                {
                    const { top, bottom } = createPathSegments(transformedPoints);
                    drawSmoothPath(top, ctx);
                    drawSmoothPath(bottom, ctx);

                    const lines = [
                        { start: transformedPoints[0], end: transformedPoints[5] },
                        { start: transformedPoints[2], end: transformedPoints[3] },
                        { start: transformedPoints[6], end: transformedPoints[1] },
                        { start: transformedPoints[1], end: transformedPoints[7] },
                        { start: transformedPoints[8], end: transformedPoints[4] },
                        { start: transformedPoints[4], end: transformedPoints[9] }
                    ];
                    lines.forEach((line) => {
                        drawLinePath(line, ctx);
                    });
                }

                ctx.stroke();
                ctx.restore();
            },
            skipTargetFind: true
        });
    },
    point: (fabric, pointIndex, fabricObject) => {
        return new fabric.Control({
            actionName: 'modifyPath',
            pointIndex: pointIndex,
            cursorStyleHandler: () => 'cell',
            positionHandler: positionHandler,
            mouseDownHandler: fabricObject.setLastPoints.bind(fabricObject),
            actionHandler: createShiftConstraint((event, data, x, y) => {
                const target = data.target;
                const control = target.controls[target.__corner];
                if (!control) return false;
                const pointIndex = control.pointIndex;
                const point = target.points[pointIndex];
                const calculateOffset = (e) => ({
                    x: e.x + (x - point.x),
                    y: e.y + (y - point.y)
                });
                if (pointIndex === 1) {
                    target.points[6] = calculateOffset(target.points[6]);
                    target.points[7] = calculateOffset(target.points[7]);
                }
                if (pointIndex === 4) {
                    target.points[8] = calculateOffset(target.points[8]);
                    target.points[9] = calculateOffset(target.points[9]);
                }
                let referencePoint;
                if ([8, 9].includes(pointIndex)) {
                    referencePoint = target.points[4];
                } else if ([6, 7].includes(pointIndex)) {
                    referencePoint = target.points[1];
                }

                if (referencePoint) {
                    const updateBezierHandle = (index, reference) => {
                        const vector = {
                            x: reference.x - x,
                            y: reference.y - y
                        };

                        const currentPoint = target.points[index];
                        const scale =
                            Math.hypot(reference.x - currentPoint.x, reference.y - currentPoint.y) /
                            Math.hypot(vector.x, vector.y);

                        target.points[index] = {
                            x: vector.x * scale + reference.x,
                            y: vector.y * scale + reference.y
                        };
                    };

                    updateBezierHandle(pointIndex + (pointIndex % 2 === 0 ? 1 : -1), referencePoint);
                }

                // Update control point position
                target.points[pointIndex] = { x, y };

                // Update the transform points
                transformPointsDebounced(target);

                return true;
            }, pointIndex),
            render: (ctx, left, top) => {
                const size = 6;
                ctx.save();
                ctx.fillStyle = '#E863F4';
                ctx.beginPath();
                ctx.arc(left, top, size, 0, 2 * Math.PI, false);
                ctx.strokeStyle = '#17A191';
                ctx.lineWidth = 1;
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            }
        });
    }
};

export default WarpTransformer;
